// ** React Imports
import { useCallback, useMemo, useState } from "react";

// ** Redux Imports
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import ShowMoreButton from "components/layout/ShowMoreButton";

// ** Location Chips Components
import LocationsListingLink from "./components/LocationsListingLink";
import LocationLink from "./components/LocationLink";

// ** Utils & Helpers
import generatePath from "utils/generatePath";

// ** Styled Components
import { StyledArrow } from "components/other/styles";

// #####################################################

const MAX_INITIAL_CITIES = 15;

// #####################################################

const LocationChipsCityLevel = ({
	country = {},
	region = {},
	city = {},
	path,
	queryString,
	withOtherCities = true,
}) => {
	const { t } = useTranslation("common");
	// ** Lista Regionów w Wybranym Kraju
	const citiesByRegionArray =
		useSelector((state) => state.mainPageUsers.location_search.cities) ||
		[];

	const [expanded, setExpanded] = useState(false);

	// ** Funkcja Renderująca Link Do Wybranego Kraju
	const renderCountryLink = useCallback(() => {
		const generatedPath = generatePath(path, {
			country: country?.url,
		});
		if (country?.url) {
			return (
				<>
					{/* Strzałka */}
					<StyledArrow />
					<LocationLink
						name={country?.name}
						to={generatedPath}
						query={queryString}
						isLast
					/>
				</>
			);
		}
		return null;
	}, [country?.url, queryString]);

	// ** Funkcja Renderująca Link Do Wybranego Regionu
	const renderRegionLink = useCallback(() => {
		const generatedPath = generatePath(path, {
			country: country?.url,
			region: region?.url,
		});
		if (region?.url) {
			return (
				<>
					{/* Strzałka */}
					<StyledArrow />
					<LocationLink
						name={region?.name}
						to={generatedPath}
						query={queryString}
						isLast
					/>
				</>
			);
		}
		return null;
	}, [region?.url, queryString]);

	// ** Funkcja Renderująca Link Do Wybranego Miasta
	const renderCityLink = useCallback(() => {
		const generatedPath = generatePath(path, {
			country: country?.url,
			region: region?.url,
			city: city?.url,
		});

		if (city?.url) {
			return (
				<>
					{/* Strzałka */}
					<StyledArrow />
					<LocationLink
						name={city?.name}
						to={generatedPath}
						query={queryString}
						isLast
						isBold
					/>
				</>
			);
		}
		return null;
	}, [city?.url, queryString]);

	// Obliczamy filtrowane i sortowane miasta bezpośrednio
	const filteredAndSortedCities = useMemo(() => {
		if (!citiesByRegionArray?.length) return [];
		return [...citiesByRegionArray]
			.filter((cityItem) => cityItem?.name !== city?.name)
			.sort((a, b) =>
				a.name.localeCompare(b.name, "pl", { sensitivity: "base" })
			);
	}, [citiesByRegionArray, city?.name]);

	// Określamy, które miasta wyświetlić
	const citiesToDisplay = useMemo(() => {
		if (expanded) {
			return filteredAndSortedCities;
		}
		return filteredAndSortedCities.slice(0, MAX_INITIAL_CITIES);
	}, [expanded, filteredAndSortedCities]);

	const citiesHidden = useMemo(() => {
		return filteredAndSortedCities.slice(MAX_INITIAL_CITIES);
	}, [filteredAndSortedCities]);

	// Czy przycisk "Pokaż więcej" powinien być widoczny
	const showMoreButtonVisible =
		filteredAndSortedCities.length > MAX_INITIAL_CITIES;

	// #####################################################

	return (
		<Block flex column withRowGap>
			<Block flex flexWrap>
				{/* Link 'Użytkownikcy / Lokalizacje */}
				<LocationsListingLink path={path} queryString={queryString} />

				{/* Link do wybranego kraju */}
				{renderCountryLink()}

				{/* Link do wybranego regionu */}
				{renderRegionLink()}

				{/* Link do wybranego miasta */}
				{renderCityLink()}
			</Block>
			{withOtherCities && citiesByRegionArray.length > 0 && (
				<Block flex column withRowGap={"var(--dz-spacing-xs)"}>
					<Block>
						<Text small bold>
							{t("common:breadcrumbs.citiesInRegion")}
						</Text>
					</Block>
					<Block
						flex
						flexWrap
						withColumnGap={"var(--dz-spacing-xxs)"}
						withRowGap={"var(--dz-spacing-xxs)"}
					>
						{citiesToDisplay.map((cityItem, index) => {
							const generatedPath = generatePath(path, {
								country: country?.url,
								region: region?.url,
								city: cityItem?.url,
							});
							return (
								<LocationLink
									key={`city-${cityItem.id}`}
									name={cityItem?.name}
									to={generatedPath}
									query={queryString}
									isBold={cityItem?.popular}
									isLast={
										index === citiesToDisplay.length - 1
									}
									onClick={() => setExpanded(false)}
								/>
							);
						})}
						{/* Dodajemy "..." jeśli lista jest zwinięta i są dodatkowe miasta */}
						{!expanded && showMoreButtonVisible && (
							<Text small>...</Text>
						)}
						{!expanded && (
							<Block hide>
								{citiesHidden.map((cityItem, index) => {
									const generatedPath = generatePath(path, {
										country: country?.url,
										region: region?.url,
										city: cityItem?.url,
									});
									return (
										<LocationLink
											key={`city-${cityItem.id}`}
											name={cityItem?.name}
											to={generatedPath}
											query={queryString}
											isBold={cityItem?.popular}
											isLast={
												index ===
												citiesToDisplay.length - 1
											}
											onClick={() =>
												setExpanded(!expanded)
											}
										/>
									);
								})}
							</Block>
						)}
					</Block>
					{showMoreButtonVisible && (
						<Block flex justifyEnd>
							<ShowMoreButton
								onClick={() => setExpanded(!expanded)}
							>
								{expanded
									? t("common:showLess")
									: t("common:showMore")}
							</ShowMoreButton>
						</Block>
					)}
				</Block>
			)}
		</Block>
	);
};

// #####################################################

export default LocationChipsCityLevel;

// #####################################################
